import type { ReactNode } from 'react';

// Everything an HVAC Speed-to-Lead applicant or rep reads on this site, in one place, so the job
// page, the application, the onboarding and the training all say the same thing.
//
// Product facts come from the real Speed-to-Lead implementation (krovance_app, hvac-speed-to-lead):
// an automated phone call to the new lead within moments, a short set of qualifying questions, a
// Hot / Qualified / Not qualified / Unreached result, and a dashboard with the summary and the full
// call transcript. It does not book appointments, text, or email - nothing here may say it does.

export type WorkMode = 'REMOTE' | 'FIELD';

export const HVAC_REMOTE_ROLE_ID = 'hvac_speed_to_lead_remote_sales';
export const HVAC_FIELD_ROLE_ID = 'hvac_speed_to_lead_field_sales';

export const HVAC_PAY = '$300 per qualifying closed customer';
export const HVAC_PAY_DETAIL = 'You earn $300 for every customer you close who successfully pays Krovance. You are paid after Krovance receives that customer’s payment. There is no hourly pay, no salary, no guaranteed pay, and no recurring or monthly commission.';
export const HVAC_CUSTOMER_PRICE = '$699/month, no setup fee, with a 7-day free trial';

export const HVAC_WHAT_YOU_SELL = 'A lead-response system for HVAC businesses. When an HVAC company gets a new lead from its website, the Speed-to-Lead system contacts that person almost immediately, gathers the important details, and helps move the lead toward booking or the company’s next sales step. It also answers calls to the company’s business number the same way.';

export const REMOTE_WORKFLOW = [
  'Take a business from your available lead list',
  'Call the HVAC business',
  'Explain the Speed-to-Lead system',
  'Show or send the provided demo when it makes sense',
  'Try to close the customer',
  'Record exactly what happened',
  'Answer the required post-lead questions',
  'Get review and feedback from the sales manager when needed',
  'Use that feedback on the next businesses you contact',
];

export const FIELD_WORKFLOW = [
  'Take a business from your available lead list',
  'Visit that HVAC business in person',
  'Ask to speak with the owner or manager',
  'Explain the Speed-to-Lead system',
  'Show the provided demo',
  'Try to close the customer',
  'Send the follow-up email',
  'Press Feedback and fill in the short report on what happened',
  'Talk it through with your admin in the Feedback chat',
  'Use that feedback on the next businesses you visit',
];

export const WORK_CYCLE = ['Take a lead', 'Contact the business', 'Sell', 'Follow up', 'Feedback', 'Improve', 'Take the next lead'];

export interface HvacJobPage {
  modeLabel: string;
  normalLead: string[];
  afterLead: string[];
  teach: string[];
  afterApply: string[];
  needs: string[];
}

export function hvacJobPage(mode: WorkMode): HvacJobPage {
  const field = mode === 'FIELD';
  return {
    modeLabel: field ? 'Field - in person' : 'Remote - by phone',
    normalLead: field
      ? [
        'You pick an HVAC business from your lead list and drive there.',
        'You ask the front desk for the owner or manager. Often they are busy or out - you find out when to come back, write it down, and move on to the next business.',
        'When you reach the owner, you ask how fast they call back new website leads, explain the Speed-to-Lead system, and show the demo.',
        'Most owners will not buy on the first visit. Some say no, some want to think, some want you to come back. A few will buy.',
        'Before you head to the next business, you record what happened.',
      ]
      : [
        'You pick an HVAC business from your lead list and call it.',
        'Many calls go unanswered or reach someone who is not the owner. You note it and call the next business.',
        'When you reach the owner or manager, you ask how fast they call back new website leads, explain the Speed-to-Lead system, and show or send the demo.',
        'Most businesses will not buy on the first call. Some say no, some want a follow-up. A few will buy.',
        'Before you call the next business, you press Feedback and fill in what happened.',
      ],
    afterLead: [
      `The job does not end when the ${field ? 'visit' : 'call'} ends.`,
      'After every business you contact, you press Feedback, fill in a short report (dropdowns only) and talk it through with your admin in the Feedback chat.',
      'Depending on what happened, you may schedule a follow-up, get feedback from your admin, or ask your admin a question - all in the Feedback chat.',
      `Then you change how you approach similar businesses next time, and take your next lead.`,
    ],
    teach: [
      'What the Speed-to-Lead system does and what the HVAC company gets',
      'How to explain it in plain words',
      'How to show the demo',
      'How to talk about the price and ask for the sale',
      'What you can and cannot promise',
      'How to take leads, follow up, and use Feedback',
    ],
    afterApply: [
      'A person at Krovance reads your application - usually within a few days - and you get an email with the decision.',
      'If you are approved, you accept the position, sign the contractor agreement, and fill out the tax form.',
      'Then you do the training and your first lead in your workspace. After that you are active.',
    ],
    needs: field
      ? ['18 or older', 'Reliable transportation to get to businesses near you', 'A phone', 'Willing to walk into businesses and talk to people you do not know']
      : ['18 or older', 'A phone and a quiet place to make calls', 'Willing to call businesses that do not know you'],
  };
}

// ---------------------------------------------------------------------------
// Application questions. The server validates the same keys (server/src/lib/hvacApplication.ts)
// and the admin reads the answers under these same words.
// ---------------------------------------------------------------------------

export type HvacFieldType = 'text' | 'textarea' | 'date' | 'yesno' | 'confirm';
export interface HvacQuestion { key: string; label: string; type: HvacFieldType; help?: string }

const ABOUT_YOU: HvacQuestion[] = [
  { key: 'fullName', label: 'Full legal name', type: 'text' },
  { key: 'isAtLeast18', label: 'Are you at least 18 years old?', type: 'yesno' },
  { key: 'timeZone', label: 'Your time zone', type: 'text', help: 'For example: Eastern, Central, Mountain, Pacific.' },
  { key: 'earliestStartDate', label: 'When could you start?', type: 'date' },
  { key: 'weeklyAvailability', label: 'About how many hours a week can you put into this, and when?', type: 'textarea' },
];

const HOW_YOU_WORK: HvacQuestion[] = [
  { key: 'whyCommissionFits', label: 'Why does commission-only sales work fit what you are looking for right now?', type: 'textarea' },
  { key: 'fiveNosInARow', label: 'If five businesses in a row tell you no, what would you do next?', type: 'textarea' },
  { key: 'productQuestionUnknown', label: 'A business asks you something about the product and you do not know the answer. What would you do?', type: 'textarea' },
  { key: 'pitchConfusingFeedback', label: 'Your sales manager tells you part of your pitch is confusing customers. What would you do differently?', type: 'textarea' },
  { key: 'recordingEveryLead', label: 'After every business you contact, you have to record what happened. How will you make sure you do this every time?', type: 'textarea' },
  { key: 'keptGoingWhenFrustrating', label: 'Tell us about a time you kept working at something even when it was frustrating or not working at first.', type: 'textarea', help: 'It can be from anywhere - a job, school, sports, family, anything.' },
];

const PAY_CONFIRMATIONS: HvacQuestion[] = [
  { key: 'confirmsIndependentContractor', label: 'I understand I would be an independent contractor, not an employee.', type: 'confirm' },
  { key: 'confirmsCommissionOnly', label: 'I understand this job is commission-only.', type: 'confirm' },
  { key: 'confirmsNoGuaranteedPay', label: 'I understand there is no guaranteed pay - no hourly wage and no salary.', type: 'confirm' },
  { key: 'confirmsPaidOnlyOnPaidCustomers', label: 'I understand I earn $300 only when a customer I close successfully pays Krovance.', type: 'confirm' },
  { key: 'confirmsNoRecurringCommission', label: 'I understand there is no recurring or monthly commission.', type: 'confirm' },
];

const JOB_CONFIRMATIONS: HvacQuestion[] = [
  { key: 'confirmsRecordEveryLead', label: 'I will record what happened after every business I contact, and answer the post-lead questions.', type: 'confirm' },
  { key: 'confirmsOnlyApprovedStatements', label: 'I will only say what Krovance has approved, and ask my sales manager when I do not know.', type: 'confirm' },
];

export function hvacQuestionSections(mode: WorkMode): Array<{ title: string; intro?: string; questions: HvacQuestion[] }> {
  const field = mode === 'FIELD';
  return [
    { title: 'About you', questions: [...ABOUT_YOU, field
      ? { key: 'hasReliableTransportation', label: 'Do you have reliable transportation to get to businesses in your area?', type: 'yesno' }
      : { key: 'hasPhoneAndQuietPlace', label: 'Do you have a phone and a quiet place where you can make calls?', type: 'yesno' }] },
    { title: 'How you would do the job', intro: 'There are no right words here and no experience is needed. Answer honestly, in your own words - short answers are fine.', questions: [
      ...HOW_YOU_WORK,
      field
        ? { key: 'ownerBusyScenario', label: 'You visit an HVAC business and the person at the front desk says the owner is busy. What would you do?', type: 'textarea' }
        : { key: 'firstCallsNoAnswerScenario', label: 'You have a list of HVAC businesses to call. The first several calls either do not answer or tell you they are not interested. What would you do?', type: 'textarea' },
    ] },
    { title: 'Confirm how you are paid', intro: HVAC_PAY_DETAIL, questions: PAY_CONFIRMATIONS },
    { title: 'Confirm what the job is', intro: field
      ? 'You visit HVAC businesses in person. You repeat one cycle: take a lead, contact the business, sell, follow up, fill in Feedback and get feedback, improve, take the next lead.'
      : 'You call HVAC businesses by phone. You repeat one cycle: take a lead, contact the business, sell, follow up, fill in Feedback and get feedback, improve, take the next lead.', questions: [
      ...(field
        ? [
          { key: 'confirmsVisitingInPerson', label: 'I understand I will visit HVAC businesses in person.', type: 'confirm' as const },
          { key: 'confirmsTalkingToStrangers', label: 'I understand I will start conversations with people I do not know.', type: 'confirm' as const },
          { key: 'confirmsBusyOrUninterested', label: 'I understand some businesses will be busy or not interested.', type: 'confirm' as const },
        ]
        : [
          { key: 'confirmsCallingStrangers', label: 'I understand I will be calling businesses I do not already know.', type: 'confirm' as const },
          { key: 'confirmsRejectionAndNoAnswer', label: 'I understand many calls will not be answered or will end with a no.', type: 'confirm' as const },
        ]),
      ...JOB_CONFIRMATIONS,
    ] },
  ];
}

export const HVAC_ANSWER_LABELS: Record<string, string> = Object.fromEntries(
  [...hvacQuestionSections('REMOTE'), ...hvacQuestionSections('FIELD')].flatMap((section) => section.questions.map((question) => [question.key, question.label])),
);

// ---------------------------------------------------------------------------
// Onboarding: the six steps an approved rep goes through, and where they are.
// ---------------------------------------------------------------------------

export const HVAC_ONBOARDING_STEPS = [
  'Accept the position',
  'Sign the contractor agreement',
  'Fill out your tax form',
  'Training: the product, selling it, and the demo',
  'Training: leads, follow-up and feedback',
  'Work your first lead in your workspace',
];

// ---------------------------------------------------------------------------
// Training modules, completed in order on the training page.
// ---------------------------------------------------------------------------

export interface HvacModule { key: string; title: string; body: (mode: WorkMode) => ReactNode; button: string }

export const HVAC_PRODUCT_PAGE_URL = 'https://app.krovance.com/products/hvac-speed-to-lead';

export const HVAC_MODULES: HvacModule[] = [
  {
    key: 'HVAC_PRODUCT',
    title: 'The product: HVAC Speed-to-Lead',
    button: 'I understand the product',
    body: () => <>
      <h3>The problem</h3>
      <p>A homeowner with a broken AC fills out the form on a few HVAC websites. The company that calls back first usually gets the job - and most call back an hour or more later.</p>
      <h3>What it does</h3>
      <ol>
        <li>A new lead submits the company’s website form.</li>
        <li>Within moments the system calls them (it says it is the company’s automated assistant).</li>
        <li>It asks the problem, how urgent it is, when they want service, their ZIP code and whether they can schedule.</li>
        <li>It sorts the lead <strong>Hot</strong>, <strong>Qualified</strong>, <strong>Not qualified</strong> or <strong>Unreached</strong> and shows it on the company’s dashboard with a summary, the full transcript and how fast the call happened.</li>
        <li>The company’s team confirms the appointment.</li>
      </ol>
      <h3>What it does not do</h3>
      <ul>
        <li>It does not book the appointment into their calendar - their team confirms it.</li>
        <li>It does not text or email the lead.</li>
        <li>It does not diagnose problems, quote prices or promise appointment times.</li>
      </ul>
      <h3>Price</h3>
      <p><strong>{HVAC_CUSTOMER_PRICE}</strong> - the only price you ever quote.</p>
    </>,
  },
  {
    key: 'HVAC_SELLING',
    title: 'Selling it',
    button: 'I understand how to sell it',
    body: (mode) => <>
      <ol>
        {mode !== 'FIELD' && <li><strong>Use the call script on every call</strong>: the full word-for-word script - gatekeeper, decision-maker, pitch and every objection - is in your workspace (<a href="https://portal.krovance.com" target="_blank" rel="noopener noreferrer">portal.krovance.com</a>) → <strong>Materials</strong> tab → <strong>Call script</strong> (the first tab). Have it open while you call. <strong>For your first few calls, do not improvise: follow the script exactly, word for word.</strong> The steps below are the short version.</li>}
        <li><strong>Contact the owner first</strong>: every lead shows the owner&apos;s name and direct number in the <strong>Owner</strong> column of your Leads tab. {mode === 'FIELD' ? 'Call that number or ask for them by name' : 'Call that number'} before you try the business&apos;s main line. Reaching the decision-maker is what matters, and contacting the business directly gives you a lower chance of reaching them.</li>
        <li><strong>Ask</strong> ({mode === 'FIELD' ? 'to the owner or manager, in person' : 'once you have the owner or manager on the phone'}): “When a new lead comes in from your website, how fast does someone call them back?”</li>
        <li><strong>Explain</strong>: “Homeowners book whoever calls first. Our system calls every new lead within moments, asks your questions, and puts the answers on your dashboard - your team just confirms the job.”</li>
        <li><strong>Show the demo</strong> (next section).</li>
        <li><strong>Give the price</strong>: “$699 a month, no setup fee - and it starts with a 7-day free trial.” Then stop talking.</li>
        <li><strong>Ask for the sale</strong>: “Do you want to get this set up?” If they need time, agree on a follow-up date.</li>
      </ol>
      <h3>Never promise</h3>
      <ul>
        <li>A number of extra jobs, bookings or dollars.</li>
        <li>Calendar booking, texting or emailing leads, or a connection to their CRM or other software.</li>
        <li>Discounts, a longer free trial, other prices, contract terms or a go-live date.</li>
        <li>That a real person makes the calls.</li>
      </ul>
      <p>Not sure? Say “I’ll find out and get back to you,” then ask in <strong>Discussions</strong> in your workspace.</p>
    </>,
  },
  {
    key: 'HVAC_DEMO',
    title: 'The demo',
    button: 'I understand the demo',
    body: (mode) => <>
      <p>The demo is on the product page: <a href={HVAC_PRODUCT_PAGE_URL} target="_blank" rel="noopener noreferrer">{HVAC_PRODUCT_PAGE_URL}</a>. It has two parts:</p>
      <ul>
        <li><strong>The live test form</strong> (“Test Speed-to-Lead Free”): the owner fills it in as a homeowner, with their own phone number - and gets the real call within moments. This is the strongest demo: they experience it themselves.</li>
        <li><strong>The 2-minute demo video</strong>: shows a lead coming in, the call, and the result on the dashboard.</li>
      </ul>
      <h3>How to use it</h3>
      <ol>
        <li>Ask: “Can I show you what happens when a lead comes in?”</li>
        <li>{mode === 'FIELD' ? 'Open the page on your phone or tablet and have them fill in the test form there and then.' : 'Send them the page link while you are on the call and have them fill in the test form - stay on the line while their phone rings. If they would rather look later, send the link and set a time to talk again.'}</li>
        <li>Point out how fast the call came and the questions it asked. Play the video if they want to see the dashboard side.</li>
        <li>Ask: “If every one of your website leads got that call, would that help you?”</li>
      </ol>
    </>,
  },
  {
    key: 'HVAC_LEADS_REPORTING',
    title: 'Leads, follow-up and feedback',
    button: 'I understand the lead routine',
    body: (mode) => <>
      <p>Everything happens in your workspace at <a href="https://portal.krovance.com" target="_blank" rel="noopener noreferrer">portal.krovance.com</a>, signed in with this Google account.</p>
      <ol>
        <li><strong>Take a lead</strong>: <strong>Leads → Find Leads</strong> (already set up for HVAC) - claim one business.</li>
        <li><strong>Contact it</strong>: {mode === 'FIELD' ? 'visit the business, then mark the lead Visited.' : <>call from your workspace phone line (request it under Calls the first time), following the call script in <strong>Materials → Call script</strong>. Calls are logged automatically.</>}</li>
        <li><strong>Follow up by email</strong>: every follow-up is an email from the <strong>Email</strong> tab - open the <strong>Templates</strong> dropdown and choose <strong>Follow Up Template</strong> to start it. Send it after every {mode === 'FIELD' ? 'visit' : 'call'}{mode === 'FIELD' ? '' : ' - even if the number did not work'}.</li>
        <li><strong>Feedback - every time</strong>: as soon as the call is done, press <strong>Feedback</strong> on the lead. Fill in your <strong>Performance Form</strong> about what happened (press the (i) next to a question if you are not sure what it means), then the Feedback chat with your admin opens. It goes into your performance, and your admin can correct it. Then send the business a quick email straight away so you are in their inbox. Send your message and come back later - a red dot shows when they reply. If nobody replies within 30 minutes, email tyler@krovance.com.</li>
        <li><strong>Feedback completes when your admin finishes it</strong>: the lead then moves on to Communicate - keep in touch with the business and follow up. You can have at most 3 leads with no feedback from your admin before you can take more - a lead stops counting once your admin has replied in its Feedback chat.</li>
        <li><strong>Keep the Performance Form up to date</strong>: if you make a new attempt on a lead (for example you call back and reach the decision-maker), they email you, or anything else changes, press <strong>Performance Form</strong> at the top of the Feedback chat and update it - a red dot reminds you when a lead emails in or a call to it is answered.</li>
        <li><strong>When feedback isn&apos;t given</strong>: feedback won&apos;t be given on a lead marked <strong>Didn&apos;t reach decision-maker</strong>, or on a lead whose situation is the same as another lead you already got feedback on.</li>
        <li><strong>Follow-ups</strong>: when another attempt is needed, pick the date and time on the Performance Form. The lead counts down to it in <strong>Leads</strong>, and your call or email to them at that time completes it.</li>
      </ol>
      <h3>If someone buys</h3>
      <p>Tell your admin in the Feedback chat right away. Krovance confirms the sale and marks the lead closed - you cannot close it yourself. You are paid your $300 after Krovance receives the payment.</p>
    </>,
  },
];

export const HVAC_PORTAL_STEP_TITLES: Record<string, string> = {
  REMOTE_LEAD_IMPORTED: 'Claim your first HVAC business',
  REMOTE_VOIP_ISSUED: 'Get your calling line',
  REMOTE_CALL_LOGGED: 'Make your first call',
  REMOTE_NOTES_COMPLETED: 'Feedback from your admin',
  REMOTE_EMAIL_CONSTRUCTOR_USED: 'Send the follow-up',
  FIELD_LEAD_IMPORTED: 'Claim your first HVAC business',
  FIELD_LEAD_VISITED: 'Visit the business',
  FIELD_NOTES_COMPLETED: 'Feedback from your admin',
  FIELD_EMAIL_CONSTRUCTOR_USED: 'Send the follow-up',
  // The Cold Email outreach method (remote): no calling line or call is needed.
  CLAIM_LEAD: 'Claim your first HVAC business',
  OUTREACH_DRAFT_SAVED: 'Save your outreach email as a draft',
  INITIAL_EMAIL_SENT: 'Send your first outreach email',
  ADMIN_FEEDBACK_COMPLETE: 'Feedback from your admin',
  FOLLOW_UP_COMPLETED: 'Follow up',
};

export const HVAC_PORTAL_STEP_BODIES: Record<string, ReactNode> = {
  REMOTE_LEAD_IMPORTED: <ul>
    <li>Go to <a href="https://portal.krovance.com" target="_blank" rel="noopener noreferrer">portal.krovance.com</a> and sign in with this Google account.</li>
    <li>Open <strong>Leads → Find Leads</strong>. The search is already set up for HVAC companies - press <strong>Search Leads</strong>.</li>
    <li>Pick one HVAC business and import it (just one for now). If it says another rep already has it, pick a different one.</li>
  </ul>,
  REMOTE_VOIP_ISSUED: <ul><li>Go to the <strong>Calls</strong> tab and request your calling line. This step completes when your line is issued.</li></ul>,
  REMOTE_CALL_LOGGED: <ul>
    <li>Before you dial, open the call script: your workspace (<a href="https://portal.krovance.com" target="_blank" rel="noopener noreferrer">portal.krovance.com</a>) → <strong>Materials</strong> tab → <strong>Call script</strong> (the first tab). Keep it open and follow it on the call.</li>
    <li><strong>For your first few calls, do not improvise: follow the script exactly, word for word.</strong></li>
    <li>Call the owner first: their direct number is in the <strong>Owner</strong> column of your Leads tab. Reaching the decision-maker is what matters, and calling the business directly gives you a lower chance of reaching them. Call from your calling line (press the call icon next to the number and follow the popup).</li>
    <li><strong>Other ways to reach them are fine</strong>: to secure a lead, you may take the time to reach out in other ways if cold calling isn&apos;t working well or the business can&apos;t answer the phone at that time. As a contractor, you are not restricted to any specific outreach method.</li>
    <li>Press <strong>Sync</strong> afterwards. This step completes when the call is synced.</li>
  </ul>,
  REMOTE_NOTES_COMPLETED: <ul>
    <li>Once the call is done, open the lead and press <strong>Feedback</strong>.</li>
    <li>Fill out your <strong>Performance Form</strong> about what happened - every time - then talk it through with your admin in the Feedback chat. You can change it later: press <strong>Performance Form</strong> at the top of the Feedback chat.</li>
    <li>This step completes when your admin finishes the feedback on this lead (it then moves to Communicate) - not when you submit your form.</li>
    <li>If you make a new attempt on this lead or anything changes, press <strong>Performance Form</strong> and update it.</li>
    <li>Feedback won&apos;t be given on a lead marked <strong>Didn&apos;t reach decision-maker</strong>, or on a lead whose situation is the same as another lead you already got feedback on.</li>
  </ul>,
  REMOTE_EMAIL_CONSTRUCTOR_USED: <ul>
    <li>Follow-up is by email.</li>
    <li>Go to the <strong>Email</strong> tab and pick the lead. The To field already has their email.</li>
    <li>Use the follow-up template: open the <strong>Templates</strong> dropdown and choose <strong>Follow Up Template</strong>. It fills in the subject and body - fill in anything in [brackets], check it, and press <strong>Send Email</strong>.</li>
    <li>This step completes when the email is sent.</li>
  </ul>,
  FIELD_LEAD_IMPORTED: <ul>
    <li>Go to <a href="https://portal.krovance.com" target="_blank" rel="noopener noreferrer">portal.krovance.com</a> and sign in with this Google account.</li>
    <li>Open <strong>Leads → Find Leads</strong>, set the location to where you are working, and press <strong>Search Leads</strong>. The search is already set up for HVAC companies.</li>
    <li>Pick one open HVAC business close enough to visit and import it (just one for now).</li>
  </ul>,
  FIELD_LEAD_VISITED: <ul>
    <li>Visit the business. Ask for the owner or manager, ask how fast they call back new website leads, explain the system, and show the demo (the live test form on the product page).</li>
    <li>Get their email before you leave if you can - the follow-up is an email.</li>
    <li>Mark the lead as <strong>Visited</strong> in your workspace.</li>
  </ul>,
  FIELD_NOTES_COMPLETED: <ul>
    <li>Once the visit is done, open the lead and press <strong>Feedback</strong>.</li>
    <li>Fill out your <strong>Performance Form</strong> about what happened - every time - then talk it through with your admin in the Feedback chat. You can change it later: press <strong>Performance Form</strong> at the top of the Feedback chat.</li>
    <li>This step completes when your admin finishes the feedback on this lead (it then moves to Communicate) - not when you submit your form.</li>
    <li>If you make a new attempt on this lead or anything changes, press <strong>Performance Form</strong> and update it.</li>
    <li>Feedback won&apos;t be given on a lead marked <strong>Didn&apos;t reach decision-maker</strong>, or on a lead whose situation is the same as another lead you already got feedback on.</li>
  </ul>,
  FIELD_EMAIL_CONSTRUCTOR_USED: <ul>
    <li>Go to the <strong>Email</strong> tab and pick the lead (add the email you collected to the lead first if it has none).</li>
    <li>Use the follow-up template: open the <strong>Templates</strong> dropdown and choose <strong>Follow Up Template</strong>. It fills in the subject and body - fill in anything in [brackets], check it, and press <strong>Send Email</strong>.</li>
  </ul>,
  CLAIM_LEAD: <ul>
    <li>Go to <a href="https://portal.krovance.com" target="_blank" rel="noopener noreferrer">portal.krovance.com</a> and sign in with this Google account.</li>
    <li>Open <strong>Leads → Find Leads</strong>. The search is already set up for HVAC companies - press <strong>Get Leads</strong>.</li>
    <li>Pick one HVAC business that shows the owner&apos;s email in the <strong>Owner</strong> column and import it (just one for now). If it says another rep already has it, pick a different one.</li>
    <li>This step completes when the business is claimed. The steps below count only for this business.</li>
  </ul>,
  OUTREACH_DRAFT_SAVED: <ul>
    <li>Go to the <strong>Email</strong> tab and pick the business you claimed.</li>
    <li>Open the <strong>Templates</strong> dropdown and choose <strong>Initial Outreach Template</strong>. It fills in the subject and body, and puts the decision-maker&apos;s email in <strong>To</strong> - fill in anything in [brackets] and check it.</li>
    <li>Press <strong>Save Draft</strong>.</li>
    <li>This step completes when the draft is saved to the decision-maker&apos;s email with the Initial Outreach Template. A business with no decision-maker email cannot complete it - pick one that has one.</li>
  </ul>,
  INITIAL_EMAIL_SENT: <ul>
    <li>Open your draft on the <strong>Email</strong> tab, check it once more, and press <strong>Send Email</strong>.</li>
    <li>This step completes when the email is accepted for delivery. A saved draft, or an email that failed to send, does not count.</li>
    <li>You do not need a calling line or the owner&apos;s phone number for any of this.</li>
  </ul>,
  ADMIN_FEEDBACK_COMPLETE: <ul>
    <li>Once the email is sent, open the lead and press <strong>Feedback</strong>.</li>
    <li>Fill out your <strong>Performance Form</strong> about what happened - every time - then talk it through with your admin in the Feedback chat. You can change it later: press <strong>Performance Form</strong> at the top of the Feedback chat.</li>
    <li>This step completes when your admin finishes the feedback on this lead (it then moves to Communicate) - not when you submit your form.</li>
    <li>If you make a new attempt on this lead or anything changes, press <strong>Performance Form</strong> and update it.</li>
  </ul>,
  FOLLOW_UP_COMPLETED: <ul>
    <li>Follow up with the same business in one of these ways:</li>
    <li>Send a separate follow-up email from the <strong>Email</strong> tab (<strong>Templates</strong> → <strong>Follow Up Template</strong>, then <strong>Send Email</strong>), or</li>
    <li>Call them (optional: request a calling line on the <strong>Calls</strong> tab first, call from it, then press <strong>Sync</strong>), or</li>
    <li>Record a demo booking with them (<strong>Record demo booking</strong> on the Email tab).</li>
    <li>Your first outreach email never counts as the follow-up. This step completes when one of these is done after it.</li>
  </ul>,
};
